import { Injectable } from '@angular/core';
import { PDFDocument, rgb, pushGraphicsState, popGraphicsState, rectangle, clip, endPath, TextAlignment, StandardFonts } from 'pdf-lib';
import { removeNonAllowedCharactersFromPDF, getImageAsEmbedNew, urlToArrayBuffer } from 'src/app/utils/utils.common';

export interface TrfHeader {
  stateName?: string;
  netProvider?: string;
  vehicleOdometer?: string;
  lastFourVin?: string;
  licensePlate?: string;
  date?: string;
  driverName?: string;
  /** URL of the driver's signature image - drawn onto the page, not typed text. */
  driverSignature?: string;
  monitorName?: string;
}

export interface TripLeg {
  memberName?: string;
  pickupTime?: string;
  dropoffTime?: string;
  mbrCall?: string;
  /** URL of the member's signature image - drawn onto the page, not typed text. */
  signature?: string;
  escortName?: string;
  escortRel?: string;
  office?: string;
  legId?: string;
  client?: string;
  pickupOdo?: string;
  dropoffOdo?: string;
  comments?: string;
  transportCode?: 'A' | 'W' | 'S';
  tripStatus?: 'C' | 'NS';
}

const TEMPLATE_URL = 'assets/documents/TN_Verida_TRF_editable.pdf';
const LEGS_PER_PAGE = 7;
// x/width match the driverSignature field's own column (to the right of the
// "Driver's Signature:" label, x:388-450). Height extended well past the
// field's own 8pt down to the row's bottom border (511.78) - with "cover"
// fit, a short box forces a huge scale-up and crops most of the signature
// away, since real signature images have a lot of blank space around the
// actual pen strokes.
const DRIVER_SIGNATURE_BOX = { x: 455, y: 515, width: 63, height: 20 };

// r{row}_signature's own rect (10pt tall) has its bottom edge sitting
// exactly on the row's internal divider line (between the Name/Signature
// line and the Leg ID/Provider Comments line below it) - a signature image
// tall enough to fill that box scales to touch that divider, visually
// straddling into the row below. Trim the box from the bottom only (the top
// already sits flush against the row's own top border, with no room to
// grow upward) so the image never reaches the line.
const MEMBER_SIGNATURE_BOTTOM_CLEARANCE = 2.5;

// r{row}_signature's own rect ends just 3pt short of r{row}_escortName's
// left edge (521 vs 524) - drawImage()'s own white cover rectangle then pads
// another 3pt onto every side to hide the field's default box, which lands
// exactly on escortName's boundary with zero clearance. Trim off the right
// end so the covered/drawn area clears it with room to spare.
const MEMBER_SIGNATURE_RIGHT_CLEARANCE = 8;

// pdf-lib vertically centers a field's text within its own rect. These
// fields' rects start at the same x as their label (instead of trailing
// right after the label text), so the value renders on its own row below
// the label, left-aligned with it, instead of indented mid-cell.
const FIELD_RECT_OVERRIDES: {
  [fieldName: string]: { x: number; width: number; y?: number; height?: number; alignment?: TextAlignment };
} = {
  // y/height matched to lastFourVin's own (un-overridden) rect, which already
  // has correct, well-proportioned spacing below its label.
  netProvider: { x: 18, width: 202, y: 538, height: 11 },
  licensePlate: { x: 389, width: 263, y: 538, height: 11 },
  date: { x: 17, width: 101, y: 514, height: 16 },
  driverName: { x: 120, width: 267, y: 514, height: 16 },
};

// stateName uses a bold "HeBo" font in the template's own DA - but
// form.flatten() renders every field with one shared default font, ignoring
// each field's own DA font. Force its real appearance with an embedded bold
// font before flattening (see fillPage below).
const BOLD_FIELDS = ['stateName'];

@Injectable({
  providedIn: 'root'
})
export class VeridaTrfService {

  async generate(header: TrfHeader, legs: TripLeg[]) {
    const templateResponse = await fetch(TEMPLATE_URL);
    const templateBytes = await templateResponse.arrayBuffer();

    const pageCount = Math.max(1, Math.ceil((legs || []).length / LEGS_PER_PAGE));
    const mergedPdf = await PDFDocument.create();

    for (let i = 0; i < pageCount; i++) {
      const legsChunk = (legs || []).slice(i * LEGS_PER_PAGE, (i + 1) * LEGS_PER_PAGE);
      const filledBytes = await this.fillPage(templateBytes, header, legsChunk);
      const sourcePdf = await PDFDocument.load(filledBytes);
      const [page] = await mergedPdf.copyPages(sourcePdf, [0]);
      mergedPdf.addPage(page);
    }

    const mergedBytes = await mergedPdf.save();
    const blob = new Blob([mergedBytes], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TN-Verida-TRF-${(header.date || 'report').replace(/\//g, '-')}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  private async fillPage(templateBytes: ArrayBuffer, header: TrfHeader, legsChunk: TripLeg[]): Promise<Uint8Array> {
    const pdfDoc = await PDFDocument.load(templateBytes);
    const form = pdfDoc.getForm();

    // The template has no separate "Trip Reimbursement Form" text or field
    // anymore - stateName is now one wide, centered field holding the whole
    // title.
    this.setText(
      form,
      'stateName',
      header.stateName ? `${header.stateName} Trip Reimbursement Form` : 'Trip Reimbursement Form'
    );
    this.setText(form, 'netProvider', header.netProvider);
    this.setText(form, 'vehicleOdometer', header.vehicleOdometer);
    this.setText(form, 'lastFourVin', header.lastFourVin);
    this.setText(form, 'licensePlate', header.licensePlate);
    this.setText(form, 'date', header.date);
    this.setText(form, 'driverName', header.driverName);
    this.setText(form, 'monitorName', header.monitorName);

    // r{row}_signature is an image (the member's signature), not typed text -
    // capture its template rect now (setText's own approach) so we can draw
    // the signature image into that same box after flatten() below, once the
    // form field itself is gone.
    const signatureBoxes: { url: string | undefined; box: { x: number; y: number; width: number; height: number } }[] = [];

    legsChunk.forEach((leg, i) => {
      const row = i + 1;
      this.setText(form, `r${row}_memberName`, leg.memberName);
      this.setText(form, `r${row}_pickupTime`, leg.pickupTime);
      this.setText(form, `r${row}_dropoffTime`, leg.dropoffTime);
      this.setText(form, `r${row}_mbrCall`, leg.mbrCall);
      const signatureBox = this.getFieldBox(form, `r${row}_signature`);
      if (signatureBox) {
        signatureBoxes.push({ url: leg.signature, box: signatureBox });
      }
      this.setText(form, `r${row}_escortName`, leg.escortName);
      this.setText(form, `r${row}_escortRel`, leg.escortRel);
      this.setText(form, `r${row}_office`, leg.office);
      this.setText(form, `r${row}_legId`, leg.legId);
      this.setText(form, `r${row}_client`, leg.client);
      this.setText(form, `r${row}_pickupOdo`, leg.pickupOdo);
      this.setText(form, `r${row}_dropoffOdo`, leg.dropoffOdo);
      this.setText(form, `r${row}_comments`, leg.comments);
      this.setText(form, `r${row}_transportCode`, leg.transportCode);
      this.setText(form, `r${row}_tripStatus`, leg.tripStatus);
    });

    await removeNonAllowedCharactersFromPDF(form);

    // form.flatten() (below) generates every field's appearance using one
    // shared default font, ignoring each field's own DA font - so these
    // fields' "HeBo" (Helvetica-Bold) setting would silently render as
    // regular weight. Force their real appearance with an actual bold font
    // first; that marks them clean, so flatten() leaves them alone.
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    for (const fieldName of BOLD_FIELDS) {
      try {
        const field = form.getTextField(fieldName);
        if (field.getText()) {
          field.defaultUpdateAppearances(boldFont);
        }
      } catch (e) {
        // field not present on template - nothing to bold.
      }
    }

    // Bakes the filled-in values into the page content so the result renders
    // identically in every viewer once copied into the merged document below.
    form.flatten();

    const [page] = pdfDoc.getPages();
    await this.drawImage(pdfDoc, page, header.driverSignature, DRIVER_SIGNATURE_BOX);
    for (const { url, box } of signatureBoxes) {
      await this.drawImage(pdfDoc, page, url, box);
    }

    return pdfDoc.save();
  }

  // r{row}_signature holds an image, so it must never go through setText()
  // (which would print the raw URL as text). Read its template rect while
  // the field still exists, before flatten() removes it.
  private getFieldBox(form, fieldName: string): { x: number; y: number; width: number; height: number } | null {
    try {
      const field = form.getTextField(fieldName);
      const rect = field.acroField.getWidgets()[0].getRectangle();
      return {
        x: rect.x,
        y: rect.y + MEMBER_SIGNATURE_BOTTOM_CLEARANCE,
        width: Math.max(0, rect.width - MEMBER_SIGNATURE_RIGHT_CLEARANCE),
        height: Math.max(0, rect.height - MEMBER_SIGNATURE_BOTTOM_CLEARANCE),
      };
    } catch (e) {
      console.warn(`[VeridaTRF] signature field not found on template: "${fieldName}"`, e);
      return null;
    }
  }

  private setText(form, fieldName: string, value: string | undefined) {
    if (!value) { return; }
    try {
      const field = form.getTextField(fieldName);
      const widget = field.acroField.getWidgets()[0];

      const override = FIELD_RECT_OVERRIDES[fieldName];
      if (override) {
        const rect = widget.getRectangle();
        widget.setRectangle({
          x: override.x,
          width: override.width,
          y: override.y !== undefined ? override.y : rect.y,
          height: override.height !== undefined ? override.height : rect.height,
        });
        if (override.alignment !== undefined) {
          field.setAlignment(override.alignment);
        }
      }

      // The "Leg ID Number:" label sits notably further from its field than
      // every other row label does (~8.9pt gap vs. ~6.6pt for e.g. "Name:"),
      // same on all 7 rows - nudge it up to match the others' spacing.
      if (/_legId$/.test(fieldName)) {
        const rect = widget.getRectangle();
        widget.setRectangle({ ...rect, y: rect.y + 2.3 });
      }

      // Pick-Up/Drop-Off/MBR Call times, Pick-Up/Drop-Off Odometer, Transport
      // Code, and Trip Status are short values in narrow columns - center
      // them instead of the default left.
      if (/_(pickupTime|dropoffTime|mbrCall|pickupOdo|dropoffOdo|transportCode|tripStatus)$/.test(fieldName)) {
        field.setAlignment(TextAlignment.Center);
      }

      field.setText(String(value));
    } catch (e) {
      console.warn(`[VeridaTRF] field not found on template: "${fieldName}"`, e);
    }
  }

  private async drawImage(
    pdfDoc: PDFDocument,
    page: any,
    url: string | undefined,
    box: { x: number; y: number; width: number; height: number }
  ) {
    if (!url) {
      console.warn('[VeridaTRF] no image URL provided - skipping image draw');
      return;
    }
    try {
      let imageBytes = await this.fetchImageBytes(url);
      if (!imageBytes) { return; }

      // Signature-pad captures are a big canvas with the actual pen strokes
      // occupying a small fraction of it - "contain" fit then scales down
      // mostly-blank space, so the visible ink shrinks to almost nothing in
      // these small boxes. Crop to the ink's own bounding box first so the
      // fit above scales real signature, not surrounding whitespace.
      imageBytes = await this.trimImageWhitespace(imageBytes);

      const image = await getImageAsEmbedNew(pdfDoc, url, imageBytes);
      if (!image) {
        console.warn(`[VeridaTRF] could not embed image (unsupported format?) from "${url}"`);
        return;
      }

      // Cover the field's default border/box first so the scaled, centered
      // image (rarely a perfect aspect match) doesn't leave gaps showing it.
      // No top padding: the "Driver's Signature:" label sits right above this
      // box with almost no headroom, so padding upward paints over the label.
      const padSide = 3;
      const padBottom = 3;
      const padTop = 0;
      page.drawRectangle({
        x: box.x - padSide,
        y: box.y - padBottom,
        width: box.width + padSide * 2,
        height: box.height + padBottom + padTop,
        color: rgb(1, 1, 1),
      });

      // "contain" fit: scale by the smaller ratio so the whole image fits
      // inside the box with nothing cropped off, even if that leaves a gap
      // on one axis.
      const scale = Math.min(box.width / image.width, box.height / image.height);
      const width = image.width * scale;
      const height = image.height * scale;

      // Belt-and-suspenders: clip to the box too, so an unexpectedly-shaped
      // real signature image can never bleed outside it even if the fit
      // math above is thrown off by something unusual in the source file.
      page.pushOperators(pushGraphicsState(), rectangle(box.x, box.y, box.width, box.height), clip(), endPath());

      page.drawImage(image, {
        x: box.x + (box.width - width) / 2,
        y: box.y + (box.height - height) / 2,
        width,
        height,
      });

      page.pushOperators(popGraphicsState());
    } catch (e) {
      console.warn(`[VeridaTRF] failed to draw image from "${url}"`, e);
    }
  }

  // Crops away near-white/transparent margins so only the actual pen
  // strokes (plus a small padding) remain - signature-pad canvases are
  // typically much bigger than the ink drawn on them, and without this the
  // "contain" fit in drawImage() spends most of its scale-down on blank
  // space instead of the signature itself. Falls back to the original bytes
  // on any failure (unsupported format, fully blank image, etc).
  private async trimImageWhitespace(imageBytes: ArrayBuffer): Promise<ArrayBuffer> {
    try {
      const bitmap = await createImageBitmap(new Blob([imageBytes]));
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { return imageBytes; }
      ctx.drawImage(bitmap, 0, 0);

      const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const WHITE_THRESHOLD = 245;
      let minX = width, minY = height, maxX = -1, maxY = -1;
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const i = (y * width + x) * 4;
          const isInk = data[i + 3] > 10 && (data[i] < WHITE_THRESHOLD || data[i + 1] < WHITE_THRESHOLD || data[i + 2] < WHITE_THRESHOLD);
          if (isInk) {
            if (x < minX) { minX = x; }
            if (x > maxX) { maxX = x; }
            if (y < minY) { minY = y; }
            if (y > maxY) { maxY = y; }
          }
        }
      }

      if (maxX < minX || maxY < minY) {
        return imageBytes; // no ink detected (blank signature) - draw the original rather than nothing.
      }

      const pad = 2;
      minX = Math.max(0, minX - pad);
      minY = Math.max(0, minY - pad);
      maxX = Math.min(width - 1, maxX + pad);
      maxY = Math.min(height - 1, maxY + pad);
      const cropWidth = maxX - minX + 1;
      const cropHeight = maxY - minY + 1;

      const cropCanvas = document.createElement('canvas');
      cropCanvas.width = cropWidth;
      cropCanvas.height = cropHeight;
      const cropCtx = cropCanvas.getContext('2d');
      if (!cropCtx) { return imageBytes; }
      cropCtx.drawImage(canvas, minX, minY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

      const croppedBlob: Blob | null = await new Promise((resolve) => cropCanvas.toBlob(resolve, 'image/png'));
      if (!croppedBlob) { return imageBytes; }
      return await new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(croppedBlob);
      });
    } catch (e) {
      console.warn('[VeridaTRF] failed to trim signature whitespace - using original image', e);
      return imageBytes;
    }
  }

  // urlToArrayBuffer() sends a Cache-Control header, which turns a plain GET
  // into a preflighted request - if the bucket's CORS rule doesn't list that
  // header as allowed, the whole request gets rejected before the real GET
  // even runs. Retry with a bare, header-less fetch (no preflight) in case
  // that's what's actually failing.
  private async fetchImageBytes(url: string): Promise<ArrayBuffer | null> {
    try {
      return (await urlToArrayBuffer(url)) as ArrayBuffer;
    } catch (e) {
      console.warn(`[VeridaTRF] urlToArrayBuffer failed for "${url}", retrying with a plain fetch`, e);
    }
    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.warn(`[VeridaTRF] plain fetch for "${url}" returned status ${response.status}`);
        return null;
      }
      return await response.arrayBuffer();
    } catch (e) {
      console.warn(`[VeridaTRF] plain fetch also failed for "${url}" - likely a CORS policy on the source bucket`, e);
      return null;
    }
  }
}
